/**
 * StatCard — a coloured metric tile for the Mango — Farmer Market kit.
 *
 * Per `preview/stat-grid.html`: a tinted cell with a big display-weight value
 * (Bricolage Grotesque, 800, ~32px, tabular-nums) above a small label. The kit
 * exposes four container tones matched to the spec (leaf / mango map to MD3
 * container roles; blue / purple are the spec's fixed accent pair) plus an
 * optional leading icon and a +/- delta line. Owns its prop surface, no `sx`
 * leak. Compose multiple in a `StatGrid`.
 */
import { Box } from '@mui/material';
import { forwardRef, type ReactNode } from 'react';

import { useColorMode } from '../theme/ColorMode.js';
import { mdSysColor, radius, spacing, typography } from '../theme/tokens.js';
import { Icon } from './Icon.js';
import { Text } from './Text.js';

export type StatCardColor = 'leaf' | 'mango' | 'blue' | 'purple' | 'neutral';

export interface StatCardProps {
  /** Caption below the value, e.g. "Seasons sold". */
  label: string;
  /** Big metric value. String to allow "92.5%" / "R 12,500" etc. */
  value: ReactNode;
  /** Container tone. Default `neutral`. */
  color?: StatCardColor;
  /** Optional leading Material Symbols ligature name. */
  icon?: string;
  /** Optional delta line, e.g. "+12% vs last season". */
  delta?: string;
  /** Delta direction — tints the delta text. Default `neutral`. */
  deltaDirection?: 'up' | 'down' | 'neutral';
  /** Pass-through className for layout composition. */
  className?: string;
}

export const StatCard = forwardRef<HTMLDivElement, StatCardProps>(function StatCard(
  { label, value, color = 'neutral', icon, delta, deltaDirection = 'neutral', className },
  ref,
) {
  // Mode-aware: map tones onto MD3 system roles so the tile tints correctly in
  // both light and dark. In light these roles resolve to the same brand values
  // the static map used (secondaryContainer=leaf[100], tertiaryContainer=mango[100],
  // neutral uses a surface-container tier (light ≈ the old paper[100] off-white,
  // dark a raised charcoal) so the tile stays subtly distinct from the page.
  // `blue`/`purple` are the spec's fixed accent pair, intentionally not tokenised.
  const { mode } = useColorMode();
  const sys = mdSysColor[mode];
  const TONE: Record<StatCardColor, { bg: string; fg: string }> = {
    leaf: { bg: sys.secondaryContainer, fg: sys.onSecondaryContainer },
    mango: { bg: sys.tertiaryContainer, fg: sys.onTertiaryContainer },
    blue: { bg: '#E3EAF6', fg: '#2851A3' },
    purple: { bg: '#EEE0F4', fg: '#7A2E94' },
    neutral: { bg: sys.surfaceContainer, fg: sys.onSurface },
  };
  const DELTA_COLOR = {
    up: sys.primary,
    down: sys.error,
    neutral: sys.onSurfaceVariant,
  } as const;
  const { bg, fg } = TONE[color];
  return (
    <Box
      ref={ref}
      className={className}
      sx={{
        backgroundColor: bg,
        color: fg,
        borderRadius: `${radius.cornerMedium}px`,
        padding: `${spacing.s4}px ${spacing.s4}px`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: `${spacing.xs}px`,
        textAlign: 'center',
      }}
    >
      {icon ? <Icon name={icon} size={24} color="inherit" /> : null}
      <Box
        sx={{
          fontFamily: typography.fontFamilyDisplay,
          fontWeight: 800,
          fontSize: typography.headlineLarge.fontSize,
          lineHeight: 1.1,
          fontVariantNumeric: 'tabular-nums',
          color: 'inherit',
        }}
      >
        {value}
      </Box>
      <Text variant="label-medium" color="inherit">
        {label}
      </Text>
      {delta ? (
        <Text variant="label-small" color={DELTA_COLOR[deltaDirection]}>
          {delta}
        </Text>
      ) : null}
    </Box>
  );
});
